import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Info, Wrench } from "lucide-react";
import { toast } from "sonner";
import { getContractErrorMessage } from "@/lib/contractErrorMessages";

/**
 * LEGACY_CONTRACT_RECONCILIATION_V1 (PR2) — "Corregir importe base legacy".
 *
 * Focused action for the audited DATA CORRECTION of the legacy inline
 * installationPrice of an imported installation. This is NOT a contractual
 * change: no contractAdjustment is created and no historical milestone,
 * certification, invoice or collection row is touched. The server is the only
 * authority for the strict money grammar, the optimistic current-value check,
 * the mandatory reason and the immutable audit record.
 */

export const LEGACY_CORRECTION_DIALOG_TITLE = "Corregir importe base legacy";

function formatMoneyDisplay(value: string | null | undefined): string {
  if (value == null || value === "") return "sin importe base legacy";
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString("es-ES", { style: "currency", currency: "EUR" })
    : String(value);
}

function parseComparableAmount(value: string | null | undefined): number | null {
  if (value == null || value.trim() === "") return null;
  const raw = value.trim();
  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;
  const amount = Number(normalized);
  return Number.isFinite(amount) ? Math.round(amount * 100) : null;
}

interface Props {
  contractId: number;
  installationId: number;
  /** Stored legacy inline price as returned by the server preview (authoritative current value). */
  currentInstallationPrice: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Called after a successful correction so the parent refetches the persisted preview. */
  onCorrected: () => void;
}

export function LegacyEconomicCorrectionDialog({
  contractId,
  installationId,
  currentInstallationPrice,
  open,
  onOpenChange,
  onCorrected,
}: Props) {
  const [newValue, setNewValue] = useState("");
  const [reason, setReason] = useState("");
  const [evidenceReference, setEvidenceReference] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [appliedResult, setAppliedResult] = useState<{
    correctionId: number;
    newAmount: string;
    previousAmount: string;
    actorUserId: number;
    appliedAt: string;
    alreadyApplied: boolean;
    correctionMode:
      | "NEW_CORRECTION"
      | "EXISTING_LEGACY_CORRECTION_RECONCILIATION";
  } | null>(null);

  const correct = trpc.customers.contracts.correctLegacyEconomicBase.useMutation();
  const hasStoredBase = currentInstallationPrice != null && currentInstallationPrice !== "";

  const trimmedNewValue = newValue.trim();
  const preview = trpc.customers.contracts.previewLegacyEconomicCorrection.useQuery(
    {
      installationId,
      contractId,
      currentInstallationPrice: currentInstallationPrice ?? "",
      newInstallationPrice: trimmedNewValue,
    },
    { enabled: open && hasStoredBase && trimmedNewValue.length > 0 && !appliedResult },
  );

  const isPotentialExistingReplay =
    hasStoredBase &&
    parseComparableAmount(currentInstallationPrice) != null &&
    parseComparableAmount(trimmedNewValue) === parseComparableAmount(currentInstallationPrice);
  const isReplayCandidate =
    preview.data?.correctionMode === "EXISTING_LEGACY_CORRECTION_RECONCILIATION" &&
    preview.data.supersedeDetection.outcome === "CANDIDATE_FOUND";
  const replayNeedsHumanReview =
    preview.data?.correctionMode === "EXISTING_LEGACY_CORRECTION_RECONCILIATION" &&
    preview.data.supersedeDetection.outcome !== "CANDIDATE_FOUND";
  const replayPreviewPending =
    isPotentialExistingReplay && (!preview.data || preview.isLoading);

  useEffect(() => {
    if (open) {
      setNewValue("");
      setReason("");
      setEvidenceReference("");
      setError(null);
      setSubmitting(false);
      setAppliedResult(null);
    }
  }, [open]);

  async function handleSubmit() {
    setError(null);
    if (!hasStoredBase) {
      setError(
        "Esta instalación no tiene un importe base legacy almacenado. Esta acción corrige un importe existente; no permite dar de alta uno nuevo.",
      );
      return;
    }
    if (!newValue.trim()) {
      setError("Indica el nuevo importe base legacy.");
      return;
    }
    if (replayPreviewPending) {
      setError("Espera a que se complete la previsualización de la duplicidad antes de confirmar.");
      return;
    }
    if (replayNeedsHumanReview) {
      setError("Esta duplicidad requiere revisión humana; no se realizará ningún cambio económico automático.");
      return;
    }
    if (reason.trim().length < 3) {
      setError("El motivo de la corrección es obligatorio.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await correct.mutateAsync({
        contractId,
        installationId,
        currentInstallationPrice: currentInstallationPrice ?? "",
        newInstallationPrice: newValue.trim(),
        reason: reason.trim(),
        evidenceReference: evidenceReference.trim() || null,
      });
      setAppliedResult({
        correctionId: res.correctionId,
        newAmount: res.newAmount,
        previousAmount: res.previousAmount,
        actorUserId: res.actorUserId,
        appliedAt: res.appliedAt,
        alreadyApplied: res.alreadyApplied,
        correctionMode: res.correctionMode ?? "NEW_CORRECTION",
      });
      toast.success(
        res.correctionMode === "EXISTING_LEGACY_CORRECTION_RECONCILIATION"
          ? "Duplicidad económica resuelta; el importe base no se ha modificado."
          : res.alreadyApplied
          ? "La corrección ya estaba aplicada; no se ha aplicado una segunda vez."
          : "Corrección económica legacy aplicada con auditoría.",
      );
      onCorrected();
    } catch (e) {
      setError(getContractErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-4 w-4" /> {LEGACY_CORRECTION_DIALOG_TITLE}
          </DialogTitle>
          <DialogDescription>
            Corrección de datos auditada del importe base legacy de la instalación. No modifica el
            contrato ni hitos, certificaciones o cobros históricos: solo actualiza el precio inline
            con un registro de auditoría inmutable.
          </DialogDescription>
        </DialogHeader>

        {appliedResult ? (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-semibold">
                {appliedResult.correctionMode === "EXISTING_LEGACY_CORRECTION_RECONCILIATION"
                  ? "Duplicidad económica resuelta (sin modificar el importe base)"
                  : appliedResult.alreadyApplied
                  ? "Corrección ya aplicada (reintento sin segundo cambio)"
                  : "Corrección aplicada"}
              </span>
            </div>
            <div className="rounded-md border p-3 bg-muted/20 space-y-1 text-xs">
              <p><strong>Registro de auditoría:</strong> #{appliedResult.correctionId}</p>
              <p><strong>Importe anterior:</strong> {formatMoneyDisplay(appliedResult.previousAmount)}</p>
              <p><strong>Importe nuevo:</strong> {formatMoneyDisplay(appliedResult.newAmount)}</p>
              <p><strong>Usuario responsable:</strong> #{appliedResult.actorUserId}</p>
              <p><strong>Fecha del servidor:</strong> {new Date(appliedResult.appliedAt).toLocaleString("es-ES")}</p>
            </div>
            <p className="text-xs text-muted-foreground">
              La previsualización de vinculación se ha actualizado con los datos persistidos.
            </p>
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>Cerrar</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md border p-3 bg-muted/20 space-y-1 text-xs">
              <p>
                <strong>Importe base legacy actual (servidor):</strong>{" "}
                {formatMoneyDisplay(currentInstallationPrice)}
                {currentInstallationPrice != null && (
                  <span className="text-muted-foreground"> ({currentInstallationPrice})</span>
                )}
              </p>
            </div>

            {!hasStoredBase && (
              <Alert variant="destructive" className="py-2">
                <Info className="h-4 w-4" />
                <AlertTitle className="text-xs font-semibold">Sin importe base legacy almacenado</AlertTitle>
                <AlertDescription className="mt-1 text-xs">
                  Esta acción corrige un importe existente; no permite dar de alta uno nuevo.
                  No hay ningún importe base legacy que corregir para esta instalación.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="legacy-correction-new">Nuevo importe base (€) *</Label>
              <Input
                id="legacy-correction-new"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder="Ej. 28.228,25"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Formato canónico: 28228,25 · 28228.25 · 28.228,25. Debe ser mayor que cero.
              </p>
            </div>

            {preview.data && <LegacyCorrectionSupersedePreview preview={preview.data} />}

            <div className="space-y-1.5">
              <Label htmlFor="legacy-correction-reason">Motivo obligatorio *</Label>
              <Textarea
                id="legacy-correction-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Describe por qué el importe base legacy importado es incorrecto."
                maxLength={2000}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="legacy-correction-evidence">Referencia de evidencia (opcional)</Label>
              <Input
                id="legacy-correction-evidence"
                value={evidenceReference}
                onChange={(e) => setEvidenceReference(e.target.value)}
                placeholder="Ej. contrato firmado 2023/114, página 2"
                maxLength={500}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Solo una referencia textual; el sistema no admite aquí la subida de documentos.
              </p>
            </div>

            <Alert className="py-2 border-blue-200 bg-blue-50/50 text-blue-900">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertTitle className="text-xs font-semibold">Corrección de datos, no contractual</AlertTitle>
              <AlertDescription className="mt-1 text-xs text-blue-800">
                Si la economía comprometida sigue superando el techo del contrato tras la corrección,
                la vinculación seguirá bloqueada y se mostrará la diferencia exacta.
              </AlertDescription>
            </Alert>

            {error && <p className="text-sm text-destructive font-medium">{error}</p>}
          </div>
        )}

        {!appliedResult && (
          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
            <Button type="button" variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={
                submitting ||
                !hasStoredBase ||
                replayPreviewPending ||
                replayNeedsHumanReview
              }
              onClick={handleSubmit}
            >
              {submitting
                ? "Aplicando corrección..."
                : isReplayCandidate
                ? "Resolver duplicidad económica"
                : "Aplicar corrección auditada"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── PR A: strict duplicate-adjustment supersede preview ─────────────────────
// Extends this same dialog/flow (no duplicate dialog) with a live, read-only
// preview of what the server's strict candidate detection would do.

interface SupersedeCandidatePreview {
  id: number;
  adjustmentNumber?: number;
  title: string;
  amount: string;
  direction: string;
}

type SupersedeDetectionPreview =
  | { outcome: "NOT_APPLICABLE" }
  | { outcome: "CANDIDATE_FOUND"; candidate: SupersedeCandidatePreview }
  | { outcome: "HUMAN_REVIEW_REQUIRED"; candidateCount: number; candidates: SupersedeCandidatePreview[] };

interface LegacyCorrectionPreviewData {
  previousAmount: string;
  newAmount: string;
  differenceAmount: string;
  correctionMode?:
    | "NEW_CORRECTION"
    | "EXISTING_LEGACY_CORRECTION_RECONCILIATION";
  existingCorrection?: {
    id: number;
    previousAmount: string;
    newAmount: string;
  } | null;
  supersedeDetection: SupersedeDetectionPreview;
  currentEffectiveAmount: string;
  projectedEffectiveAmount: string;
  projectedCommittedAmount: string;
  projectedAvailableAmount: string;
  projectedDifferenceAmount: string;
}

function LegacyCorrectionSupersedePreview({ preview }: { preview: LegacyCorrectionPreviewData }) {
  const detection = preview.supersedeDetection;
  if (detection.outcome === "NOT_APPLICABLE") return null;

  if (detection.outcome === "HUMAN_REVIEW_REQUIRED") {
    return (
      <Alert className="py-2 border-amber-300 bg-amber-50/60 text-amber-900">
        <Info className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-xs font-semibold">Revisión humana requerida</AlertTitle>
        <AlertDescription className="mt-1 text-xs text-amber-900">
          {detection.candidateCount === 0
            ? "No se ha encontrado ningún ajuste histórico que coincida de forma inequívoca con esta corrección."
            : `Se han encontrado ${detection.candidateCount} ajustes históricos compatibles; no se marcará ninguno automáticamente para evitar una decisión económica oculta.`}
          {" "}Importe efectivo proyectado tras la corrección: {formatMoneyDisplay(preview.projectedEffectiveAmount)}.
        </AlertDescription>
      </Alert>
    );
  }

  const { candidate } = detection;
  const adjustmentLabel =
    candidate.adjustmentNumber != null
      ? `MC-${String(candidate.adjustmentNumber).padStart(3, "0")}`
      : `#${candidate.id}`;
  if (preview.correctionMode === "EXISTING_LEGACY_CORRECTION_RECONCILIATION") {
    return (
      <div className="rounded-md border border-blue-200 bg-blue-50/50 p-3 space-y-1 text-xs text-blue-900">
        <p className="font-semibold">Duplicidad económica detectada</p>
        {preview.existingCorrection && (
          <p>
            <strong>Corrección ya aplicada:</strong>{" "}
            {formatMoneyDisplay(preview.existingCorrection.previousAmount)} →{" "}
            {formatMoneyDisplay(preview.existingCorrection.newAmount)}
          </p>
        )}
        <p>
          <strong>Ajuste histórico duplicado:</strong> {adjustmentLabel} ·{" "}
          {candidate.title} ({candidate.direction === "increase" ? "+" : "−"}
          {formatMoneyDisplay(candidate.amount)})
        </p>
        <p>
          <strong>Acción:</strong> conservar el ajuste en auditoría y excluirlo
          de la economía vigente.
        </p>
        <p>
          <strong>Proyectado:</strong> base corregida{" "}
          {formatMoneyDisplay(preview.newAmount)} · efectivo{" "}
          {formatMoneyDisplay(preview.projectedEffectiveAmount)} · comprometido{" "}
          {formatMoneyDisplay(preview.projectedCommittedAmount)} · diferencia{" "}
          {formatMoneyDisplay(preview.projectedDifferenceAmount)}
        </p>
        <p className="text-blue-800">
          El importe base ya fue corregido anteriormente. Esta operación NO
          volverá a modificar el importe base. {candidate.title} permanecerá
          visible en auditoría pero dejará de afectar al cálculo económico
          vigente.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-blue-200 bg-blue-50/50 p-3 space-y-1 text-xs text-blue-900">
      <p className="font-semibold">Ajuste histórico coincidente detectado</p>
      <p>
        <strong>{candidate.title}</strong> ({candidate.direction === "increase" ? "+" : "−"}
        {formatMoneyDisplay(candidate.amount)})
      </p>
      <p>
        Importe efectivo actual: {formatMoneyDisplay(preview.currentEffectiveAmount)} → proyectado:{" "}
        {formatMoneyDisplay(preview.projectedEffectiveAmount)}
      </p>
      <p>
        Comprometido proyectado: {formatMoneyDisplay(preview.projectedCommittedAmount)} · Diferencia:{" "}
        {formatMoneyDisplay(preview.projectedDifferenceAmount)}
      </p>
      <p className="text-blue-800">
        Al confirmar, este ajuste permanecerá en el historial de auditoría pero dejará de sumar en el
        cálculo económico vigente.
      </p>
    </div>
  );
}

export function LegacyCorrectionAppliedBadge({ correctionId }: { correctionId: number }) {
  return <Badge variant="outline">Corrección #{correctionId}</Badge>;
}
